import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Mail } from "lucide-react";
import AnimatedNumber from "@/components/motion/AnimatedNumber";
import { fmt, periodicPayment } from "./Hero";

const FREQUENCIES = [
  { label: "Monthly", perYear: 12, suffix: "/mo" },
  { label: "Bi-Weekly", perYear: 26, suffix: "/bi-wk" },
  { label: "Weekly", perYear: 52, suffix: "/wk" },
];

function balanceAfter(principal, annualRate, years, perYear, elapsed) {
  const i = Math.pow(1 + annualRate / 100 / 2, 2 / perYear) - 1;
  const pay = periodicPayment(principal, annualRate, years, perYear);
  if (i === 0) return Math.max(principal - pay * elapsed, 0);
  return Math.max(principal * Math.pow(1 + i, elapsed) - pay * ((Math.pow(1 + i, elapsed) - 1) / i), 0);
}

function CalcField({ label, value, displayPrefix = "", displaySuffix = "", min, max, step, onChange, testId }) {
  const clamp = (v) => Math.min(Math.max(isNaN(v) ? min : v, min), max);
  return (
    <div>
      <div className="flex items-center justify-between mb-2.5">
        <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50">{label}</label>
        <div className="flex items-center gap-1.5">
          {displayPrefix && <span className="text-xs text-ivory/40">{displayPrefix}</span>}
          <input
            data-testid={`${testId}-input`}
            type="number"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => onChange(clamp(parseFloat(e.target.value)))}
            className="w-28 bg-white/5 border border-white/15 rounded-sm px-2.5 py-1 text-right font-serif text-lg text-champagne focus:outline-none focus:border-gold/50 transition-colors duration-300 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          {displaySuffix && <span className="text-xs text-ivory/40 w-7">{displaySuffix}</span>}
        </div>
      </div>
      <Slider
        data-testid={testId}
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
        className="[&_[role=slider]]:bg-gold [&_[role=slider]]:border-champagne [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5"
      />
    </div>
  );
}

function LTVRing({ ratio }) {
  const C = 2 * Math.PI * 26;
  const clamped = Math.min(Math.max(ratio, 0), 1);
  return (
    <div className="relative w-16 h-16 shrink-0" data-testid="calc-ltv-ring">
      <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
        <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
        <motion.circle
          cx="32"
          cy="32"
          r="26"
          fill="none"
          stroke="#E5C07B"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={C}
          initial={false}
          animate={{ strokeDashoffset: C * (1 - clamped) }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[11px] font-medium text-champagne">{Math.round(clamped * 100)}%</span>
        <span className="text-[7px] tracking-[0.16em] text-ivory/40">LTV</span>
      </div>
    </div>
  );
}

function ShareRing({ share }) {
  const C = 2 * Math.PI * 26;
  const clamped = Math.min(Math.max(share, 0), 1);
  return (
    <div className="relative w-20 h-20 shrink-0" data-testid="calc-share-ring">
      <svg viewBox="0 0 64 64" className="w-20 h-20 -rotate-90">
        <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(205,149,81,0.45)" strokeWidth="4" />
        <motion.circle
          cx="32"
          cy="32"
          r="26"
          fill="none"
          stroke="#E5C07B"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={C}
          initial={false}
          animate={{ strokeDashoffset: C * (1 - clamped) }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs font-medium text-champagne">{Math.round(clamped * 100)}%</span>
        <span className="text-[6.5px] tracking-[0.14em] text-ivory/40">PRINCIPAL</span>
      </div>
    </div>
  );
}

function BalanceChart({ loan, rate, amort, perYear }) {
  const W = 300;
  const H = 90;
  const path = useMemo(() => {
    const max = Math.max(loan, 1);
    let d = "";
    for (let y = 0; y <= amort; y++) {
      const b = balanceAfter(loan, rate, amort, perYear, y * perYear);
      d += `${y === 0 ? "M" : "L"}${((y / amort) * W).toFixed(1)},${(H - (b / max) * (H - 14)).toFixed(1)}`;
    }
    return d;
  }, [loan, rate, amort, perYear]);

  return (
    <div data-testid="calc-balance-chart">
      <div className="flex justify-between text-[10px] uppercase tracking-[0.16em] text-ivory/45 mb-2">
        <span>Mortgage Balance</span>
        <span>{amort} yrs</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24">
        <defs>
          <linearGradient id="bal-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E5C07B" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#E5C07B" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L${W},${H} L0,${H} Z`} fill="url(#bal-fill)" />
        <motion.path
          key={path}
          d={path}
          fill="none"
          stroke="#E5C07B"
          strokeWidth="1.5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="flex justify-between text-[10px] text-ivory/35 mt-1">
        <span>${fmt(loan)}</span>
        <span>$0</span>
      </div>
    </div>
  );
}

// Shared calculator state — identical logic on the home hero and the
// dedicated Mortgage Calculator page.
export function useMortgageCalculator() {
  const [price, setPrice] = useState(850000);
  const [down, setDown] = useState(170000);
  const [rate, setRate] = useState(4.89);
  const [amort, setAmort] = useState(25);
  const [freq, setFreq] = useState(FREQUENCIES[0]);
  const [tab, setTab] = useState("estimate");

  const loan = Math.max(price - down, 0);
  const payment = useMemo(() => periodicPayment(loan, rate, amort, freq.perYear), [loan, rate, amort, freq]);
  const totalPaid = payment * amort * freq.perYear;
  const totalInterest = Math.max(totalPaid - loan, 0);
  const interestFirst = loan * (Math.pow(1 + rate / 100 / 2, 2 / freq.perYear) - 1);
  const principalPct = payment > 0 ? Math.min(((payment - interestFirst) / payment) * 100, 100) : 0;
  const principalShare = totalPaid > 0 ? loan / totalPaid : 0;
  const ltv = price > 0 ? loan / price : 0;
  const downPct = price > 0 ? down / price : 0;

  const shareEstimate = () => {
    const subject = encodeURIComponent("My Mortgage Estimate — My First Home Mortgage Ltd");
    const body = encodeURIComponent(
      `My mortgage estimate (estimates only — not an approval or commitment to lend):\n\n` +
        `Purchase Price: $${fmt(price)}\n` +
        `Down Payment: $${fmt(down)} (${Math.round(downPct * 100)}%)\n` +
        `Interest Rate: ${rate.toFixed(2)}%\n` +
        `Amortization: ${amort} years\n` +
        `Payment Frequency: ${freq.label}\n\n` +
        `Estimated ${freq.label} Payment: $${fmt(payment)}\n` +
        `Mortgage Amount: $${fmt(loan)}\n` +
        `Total Interest (est.): $${fmt(totalInterest)}\n` +
        `Est. Total Payments: $${fmt(totalPaid)}\n\n` +
        `Calculator results are estimates for informational purposes only and do not constitute mortgage approval or a commitment to lend.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return {
    price, setPrice, down, setDown, rate, setRate, amort, setAmort,
    freq, setFreq, tab, setTab,
    loan, payment, totalPaid, totalInterest, principalPct, principalShare, ltv, downPct,
    shareEstimate,
  };
}

export function MortgageCalculator({ calc, onLead }) {
  const {
    price, setPrice, down, setDown, rate, setRate, amort, setAmort,
    freq, setFreq, tab, setTab,
    loan, payment, totalPaid, totalInterest, principalPct, principalShare, ltv, downPct,
    shareEstimate,
  } = calc;

  return (
    <div id="calculator" data-testid="mortgage-calculator" className="glass-panel rounded-md p-8 lg:p-9 scroll-mt-32">
      <p className="gold-label mb-1.5">Mortgage Calculator</p>
      <div className="flex items-end justify-between mb-7">
        <h3 className="font-serif text-2xl text-ivory">Estimate Your Payment</h3>
        <div className="flex gap-1.5">
          {["estimate", "analysis"].map((t) => (
            <button
              key={t}
              data-testid={`calc-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-3.5 py-1.5 text-[10px] tracking-[0.18em] rounded-sm border transition-colors duration-300 ${
                tab === t
                  ? "border-gold/70 text-gold bg-gold/10"
                  : "border-white/15 text-ivory/50 hover:border-gold/40 hover:text-ivory"
              }`}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {tab === "estimate" ? (
          <motion.div
            key="estimate"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <div className="space-y-6">
              <CalcField label="Purchase Price" value={price} displayPrefix="$" min={200000} max={3000000} step={10000} onChange={setPrice} testId="calc-purchase-price" />
              <CalcField label="Down Payment" value={down} displayPrefix="$" min={0} max={1500000} step={5000} onChange={setDown} testId="calc-down-payment" />
              <CalcField label="Interest Rate" value={rate} displaySuffix="%" min={1} max={10} step={0.05} onChange={setRate} testId="calc-interest-rate" />
              <CalcField label="Amortization" value={amort} displaySuffix="yrs" min={5} max={30} step={1} onChange={setAmort} testId="calc-amortization" />
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-2.5">Payment Frequency</label>
                <div className="flex gap-2" data-testid="calc-frequency">
                  {FREQUENCIES.map((f) => (
                    <button
                      key={f.label}
                      data-testid={`calc-freq-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setFreq(f)}
                      className={`flex-1 px-2 py-2 text-[10px] tracking-wide rounded-sm border transition-colors duration-300 ${
                        freq.label === f.label
                          ? "border-gold/70 text-gold bg-gold/10"
                          : "border-white/15 text-ivory/55 hover:border-gold/40 hover:text-ivory"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-8 pt-7 border-t border-white/10">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 mb-2">
                    Estimated {freq.label} Payment
                  </p>
                  <span data-testid="calc-monthly-payment">
                    <AnimatedNumber value={payment} suffix={freq.suffix} className="font-serif text-4xl gold-text-gradient" />
                  </span>
                </div>
                <span className="text-[10px] text-ivory/35 max-w-[110px] text-right leading-snug">Estimate only. Not a rate guarantee.</span>
              </div>
              <div className="mt-6 flex items-center gap-5">
                <div className="flex flex-col items-center gap-1.5">
                  <LTVRing ratio={ltv} />
                  <span data-testid="calc-down-pct" className="text-[10px] text-ivory/45">{Math.round(downPct * 100)}% down</span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between text-[10px] uppercase tracking-[0.16em] text-ivory/45 mb-2">
                    <span>Principal</span>
                    <span>Interest</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden flex" data-testid="calc-split-bar">
                    <motion.div
                      className="h-full bg-gold"
                      initial={false}
                      animate={{ width: `${principalPct}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                    <motion.div
                      className="h-full bg-bronze/70"
                      initial={false}
                      animate={{ width: `${100 - principalPct}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                  </div>
                  <p className="mt-2 text-[10px] text-ivory/35">First payment split · estimate</p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="analysis"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <div className="grid grid-cols-3 gap-4 mb-7" data-testid="calc-analysis-stats">
              <div>
                <p className="text-[9px] uppercase tracking-[0.18em] text-ivory/45 mb-1.5">Mortgage Amount</p>
                <AnimatedNumber data-testid="calc-mortgage-amount" value={loan} className="font-serif text-xl text-champagne" />
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-[0.18em] text-ivory/45 mb-1.5">Total Interest</p>
                <AnimatedNumber value={totalInterest} className="font-serif text-xl text-bronze" />
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-[0.18em] text-ivory/45 mb-1.5">Est. Total Payments</p>
                <AnimatedNumber value={totalPaid} className="font-serif text-xl text-ivory" />
              </div>
            </div>
            <div className="flex items-center gap-6 pb-7 border-b border-white/10">
              <ShareRing share={principalShare} />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.18em] text-ivory/45 mb-2">Lifetime Payment Split</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ivory/45 font-light">Principal</span>
                    <AnimatedNumber value={loan} className="text-gold" />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ivory/45 font-light">Interest</span>
                    <AnimatedNumber value={totalInterest} className="text-bronze" />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <BalanceChart loan={loan} rate={rate} amort={amort} perYear={freq.perYear} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-7 text-[10px] text-ivory/35 leading-relaxed" data-testid="calc-disclaimer">
        Calculator results are estimates for informational purposes only and do not constitute mortgage approval or a commitment to lend.
      </p>
      <button
        data-testid="calc-get-preapproved-btn"
        onClick={() => onLead("pre-approval")}
        className="mt-5 w-full px-6 py-3.5 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
      >
        GET PRE-APPROVED
      </button>
      <button
        data-testid="calc-email-estimate-btn"
        onClick={shareEstimate}
        className="mt-3 w-full inline-flex items-center justify-center gap-2.5 px-6 py-3.5 text-xs tracking-[0.2em] font-medium text-ivory/75 border border-white/20 hover:border-gold/60 hover:text-gold transition-colors duration-300 rounded-sm"
      >
        <Mail size={14} strokeWidth={1.5} /> EMAIL MY ESTIMATE
      </button>
    </div>
  );
}
